import { cn } from '@/lib/cn';

import { Avatar } from './Avatar';

export interface PersonChipProps {
  name: string | null;
  id?: string | null;
  /** Job title, or a role - whatever the screen has room for. */
  secondary?: string | null;
  size?: 'sm' | 'md';
  /** Renders initials only, with the name as the accessible label. */
  compact?: boolean;
  className?: string;
}

/**
 * A person, rendered the same way everywhere.
 *
 * `name === null` is "unassigned", and it is shown rather than hidden: an
 * unassigned task in the current week is exactly the thing a planning screen
 * needs to surface.
 *
 * Note for screens that render this from a `TaskDto`: a client never sees an
 * assignee at all - `redactTask` nulls the fields server-side - so the
 * unassigned rendering is also what a client sees, correctly.
 */
export function PersonChip({
  name,
  id,
  secondary,
  size = 'md',
  compact = false,
  className,
}: PersonChipProps) {
  if (!name) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 text-subtle',
          size === 'sm' ? 'text-small' : 'text-ui',
          className,
        )}
      >
        <span
          className={cn(
            'inline-block rounded-full border border-dashed border-line-strong',
            size === 'sm' ? 'size-7' : 'size-9',
          )}
          aria-hidden="true"
        />
        {compact ? null : <span className="italic">Unassigned</span>}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
      title={compact ? name : undefined}
    >
      <Avatar name={name} id={id ?? undefined} size={size} />
      {compact ? (
        <span className="sr-only">{name}</span>
      ) : (
        <span className="min-w-0">
          <span
            className={cn('block truncate font-medium', size === 'sm' ? 'text-small' : 'text-ui')}
          >
            {name}
          </span>
          {secondary ? (
            <span className="block truncate text-micro text-subtle">{secondary}</span>
          ) : null}
        </span>
      )}
    </span>
  );
}
